/**
 * Biometric Authentication Hook for ParachPay
 * Uses @capgo/capacitor-native-biometric for fingerprint/Face ID support
 */
import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';

const SERVER_ID = 'parachpay.com'; // Unique identifier for credential storage
const BIOMETRIC_ENABLED_KEY = 'parachpay_biometric_enabled';

export const useBiometricAuth = () => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);

  // Check if biometric auth is available on this device
  const checkAvailability = useCallback(async () => {
    // Only available on native platforms
    if (!Capacitor.isNativePlatform()) {
      setLoading(false);
      return;
    }

    try {
      const result = await NativeBiometric.isAvailable();
      setIsAvailable(result.isAvailable);
      
      // Set human-readable biometry type for UI display
      if (result.biometryType === BiometryType.FACE_ID) {
        setBiometryType('Face ID');
      } else if (result.biometryType === BiometryType.TOUCH_ID) {
        setBiometryType('Touch ID');
      } else if (result.biometryType === BiometryType.FINGERPRINT) {
        setBiometryType('Fingerprint');
      } else if (result.biometryType === BiometryType.IRIS) {
        setBiometryType('Iris');
      } else if (result.biometryType !== BiometryType.NONE) {
        setBiometryType('Biometric');
      }

      // Check if user has enabled biometric login
      const enabled = localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
      setIsEnabled(enabled);

      // Check if we have stored credentials
      if (enabled) {
        try {
          await NativeBiometric.getCredentials({ server: SERVER_ID });
          setHasStoredCredentials(true);
        } catch {
          setHasStoredCredentials(false);
        }
      }
    } catch (error) {
      console.log('[Biometric] Not available:', error.message);
      setIsAvailable(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  /**
   * Verify user identity with biometrics
   * @returns {Promise<boolean>} Whether authentication succeeded
   */
  const verifyIdentity = useCallback(async () => {
    if (!isAvailable) return false;

    try {
      await NativeBiometric.verifyIdentity({
        reason: 'Authenticate to access ParachPay',
        title: 'ParachPay Login',
        subtitle: `Use ${biometryType || 'biometrics'} to unlock`,
        description: 'Securely access your wallet',
        useFallback: true, // Allow PIN/password as fallback
      });
      return true;
    } catch (error) {
      console.log('[Biometric] Verification failed:', error.message);
      return false;
    }
  }, [isAvailable, biometryType]);

  /**
   * Store user credentials securely after successful login
   * @param {string} email - User's email
   * @param {string} password - User's password
   */
  const storeCredentials = useCallback(async (email, password) => {
    if (!isAvailable) return false;

    try {
      await NativeBiometric.setCredentials({
        username: email,
        password: password,
        server: SERVER_ID,
      });
      localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
      setIsEnabled(true);
      setHasStoredCredentials(true);
      return true;
    } catch (error) {
      console.error('[Biometric] Failed to store credentials:', error);
      return false;
    }
  }, [isAvailable]);

  /**
   * Retrieve stored credentials after biometric verification
   * @returns {Promise<{email: string, password: string} | null>}
   */
  const getStoredCredentials = useCallback(async () => {
    if (!isAvailable || !hasStoredCredentials) return null;

    try {
      // First verify identity
      const verified = await verifyIdentity();
      if (!verified) return null;

      // Then retrieve credentials
      const credentials = await NativeBiometric.getCredentials({
        server: SERVER_ID,
      });

      return {
        email: credentials.username,
        password: credentials.password,
      };
    } catch (error) {
      console.error('[Biometric] Failed to get credentials:', error);
      return null;
    }
  }, [isAvailable, hasStoredCredentials, verifyIdentity]);

  /**
   * Delete stored credentials and disable biometric login
   */
  const deleteCredentials = useCallback(async () => {
    try {
      await NativeBiometric.deleteCredentials({ server: SERVER_ID });
    } catch (error) {
      console.log('[Biometric] No credentials to delete');
    }
    
    localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
    setIsEnabled(false);
    setHasStoredCredentials(false);
  }, []);

  /**
   * Enable biometric authentication for future logins
   * Call this after a successful login with email/password
   */
  const enableBiometric = useCallback(async (email, password) => {
    if (!isAvailable) {
      throw new Error('Biometric authentication is not available on this device');
    }

    // Verify identity first to ensure user has enrolled biometrics
    const verified = await verifyIdentity();
    if (!verified) {
      throw new Error('Biometric verification failed');
    }

    // Store credentials
    const stored = await storeCredentials(email, password);
    if (!stored) {
      throw new Error('Failed to store credentials');
    }

    return true;
  }, [isAvailable, verifyIdentity, storeCredentials]);

  /**
   * Disable biometric authentication
   */
  const disableBiometric = useCallback(async () => {
    await deleteCredentials();
  }, [deleteCredentials]);

  return {
    // State
    isAvailable,
    biometryType,
    isEnabled,
    loading,
    hasStoredCredentials,
    
    // Methods
    verifyIdentity,
    getStoredCredentials,
    enableBiometric,
    disableBiometric,
    
    // Re-check availability (useful after app resume)
    refresh: checkAvailability,
  };
};

export default useBiometricAuth;
